/**
 * WAF Overview dashboard — the Owl homeAnalytics + historicalReport + billing
 * roll-up for the whole org or a single site/domain. A two-level scope (Site →
 * Domain) and a date-range preset drive every panel. Each panel is its own
 * component (RoutingCard / BillingCard / QuotaCard / MySiteCard) and the scope
 * uses the shared WafScopeBar; this file only wires the scope + range and lays
 * them out.
 *
 * Scope resolves to org-overview (both empty) | a site (`site_domain`/`site_id`)
 * | a single domain (`site_domain` = the subdomain FQDN; historical stays at the
 * site granularity it supports).
 */
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LayoutDashboard } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { KpiTiles, RefreshButton, Stack } from '@/pages/services/_shared/AnalyticsKit';
import {
  useWafDnsZones,
  useWafHomeReport,
  useWafTopOptimizations,
  type WafDnsZone,
  type WafHomeScope,
} from '@/api/waf';
import { WafRangeControl, type ResolvedRange } from '../analytics/WafRangeControl';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import { fmtBytes, fmtNum, fmtPct } from './format';
import { TrafficChartCard } from './TrafficChartCard';
import { RoutingCard, routeCats } from './RoutingCard';
import { TopDomainsCard } from './TopDomainsCard';
import { BillingCard } from './BillingCard';
import { QuotaCard } from './QuotaCard';
import { MySiteCard } from './MySiteCard';
import styles from './WafDashboard.module.css';

export function WafDashboard({ serviceId }: { serviceId: string }) {
  // Scope (Site → Domain) is owned by the shared WafScopeBar, which persists it.
  // Empty siteId = org-wide "All site"; empty domain = the site's "All domain".
  const [siteId, setSiteId] = useState('');
  const [domain, setDomain] = useState('');
  const [range, setRange] = useState<ResolvedRange | null>(null);

  const onScope = (s: WafScope) => {
    if (s.allSites) {
      setSiteId('');
      setDomain('');
    } else {
      setSiteId(s.zone?.id ?? '');
      setDomain(s.allDomains ? '' : (s.subdomain ?? ''));
    }
  };

  const zonesQ = useWafDnsZones(serviceId);
  const siteDomain = useMemo(() => {
    if (!siteId) return '';
    if (domain) return domain; // a specific subdomain FQDN
    return (zonesQ.data?.items ?? []).find((z: WafDnsZone) => z.id === siteId)?.domain ?? '';
  }, [siteId, domain, zonesQ.data]);

  const scope: WafHomeScope = useMemo(
    () => ({ siteDomain, siteId: siteId || '', startTime: range?.start_time ?? 0, endTime: range?.end_time ?? 0 }),
    [siteDomain, siteId, range],
  );

  const reportQ = useWafHomeReport(serviceId, scope);
  const routingQ = useWafTopOptimizations(serviceId, scope, routeCats(scope));
  const summary = reportQ.data;

  // Refetch every dashboard panel (home / billing / sites).
  const qc = useQueryClient();
  const refresh = () => {
    for (const k of ['home', 'feature-usage', 'credit', 'domains']) {
      qc.invalidateQueries({ queryKey: ['waf', serviceId, k] });
    }
  };

  const kpis = [
    { label: 'Traffic', value: summary ? fmtBytes(summary.total_traffic_bytes) : '—' },
    { label: 'Requests', value: summary ? fmtNum(summary.total_requests) : '—' },
    { label: 'Routing Decision', value: routingQ.data ? fmtNum(routingQ.data.total_count) : '—' },
    { label: 'Cache Hit Rate', value: summary ? fmtPct(summary.cache_hit_rate * 100) : '—' },
  ];

  return (
    <Container size="xl">
      <WafScopeBar
        serviceId={serviceId}
        mode="subdomain"
        allowAllSites
        allowAllSubdomains
        defaultAllSites
        storeKey={`waf:${serviceId}:dashboard:scope`}
        onChange={onScope}
        className={styles.scope}
      />

      <div className={styles.headRow}>
        <PageHeader
          icon={<LayoutDashboard size={22} />}
          title="Overview"
          description="Metrics and general site status collected from your Managed CDNs."
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <WafRangeControl serviceId={serviceId} pageKey="dashboard" onChange={setRange} />
          <RefreshButton onRefresh={refresh} />
        </div>
      </div>

      <Stack gap="var(--vn-space-4)">
        <KpiTiles items={kpis} loading={reportQ.isPending && !summary} />

        <div className={styles.cols}>
          <Stack gap="var(--vn-space-4)">
            <TrafficChartCard serviceId={serviceId} scope={scope} />
            {/* Top domains under a single site (a specific site + All domain). */}
            {siteId && !domain && <TopDomainsCard serviceId={serviceId} scope={scope} />}
            <RoutingCard serviceId={serviceId} scope={scope} />
          </Stack>
          <div className={styles.side}>
            <BillingCard serviceId={serviceId} />
            <QuotaCard serviceId={serviceId} />
          </div>
        </div>

        <MySiteCard serviceId={serviceId} />
      </Stack>
    </Container>
  );
}

export default WafDashboard;
